import { AlertTriangle, DollarSign } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const stats = [
  {
    icon: AlertTriangle,
    stat: "775",
    headline: "Advertising complaints assessed",
    label:
      "AHPRA assessed 775 advertising complaints in 2024/25 — the highest on record. Source: AHPRA Annual Report 2024/25.",
  },
  {
    icon: DollarSign,
    stat: "$120K",
    headline: "Maximum penalty per offence",
    label:
      "Penalties reach $60,000 for individuals and $120,000 for body corporates per offence. AHPRA now uses AI to scan practitioner websites and social media.",
  },
];

const PractitionerProblem = () => {
  return (
    <section className="py-16 sm:py-28 bg-background">
      <div className="container max-w-6xl px-5 sm:px-6 lg:px-8">
        <p className="section-label mb-4 text-left">THE COMPLIANCE GAP</p>
        <div className="teal-rule mb-6 sm:mb-8" />

        <h2 className="text-2xl sm:text-4xl font-bold text-foreground mb-5 sm:mb-6 text-left max-w-3xl">
          Practitioners are publishing faster than compliance can keep up with.
        </h2>

        <div className="text-text-body text-base sm:text-lg mb-10 sm:mb-16 text-left max-w-3xl leading-relaxed space-y-4">
          <p>
            Most practitioners receive training on advertising compliance — but it's only a fraction of their health degree. Clinical practice, patient care, and modality-specific skills take priority. Advertising compliance is rarely the focus, and knowledge fades over time.
          </p>
          <p>
            CompliGUARD removes the friction from advertising compliance — so practitioners can spend that time where it matters.
          </p>
          <p>
            Most practitioners don't think about advertising compliance until something goes wrong. A complaint. A flagged post. An AHPRA letter. By that point, the content is already live — already archived, already screenshotted. And the record of what was published, and when, is often unclear.
          </p>
          <p>
            This isn't a competence problem. It's an infrastructure problem. And enforcement is increasing.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          {stats.map((item) => (
            <div
              key={item.stat}
              className="bg-card p-6 sm:p-8 border border-border card-accent-left rounded-sm text-left shadow-[0_4px_16px_rgba(11,31,53,0.08)] card-hover"
            >
              <item.icon className="h-12 w-12 sm:h-16 sm:w-16 text-destructive mb-3 sm:mb-4 opacity-90" />
              <div className="text-2xl sm:text-3xl font-extrabold text-foreground mb-1">
                {item.stat}
              </div>
              <div className="text-sm font-semibold text-foreground mb-2">
                {item.headline}
              </div>
              <p className="text-text-caption text-sm leading-relaxed">
                {item.label}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-10 sm:mt-14 bg-card border border-border card-accent-left-gold rounded-sm p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="max-w-xl">
            <p className="text-base sm:text-lg font-semibold text-foreground mb-1">
              How exposed is your current marketing?
            </p>
            <p className="text-text-caption text-sm leading-relaxed">
              Take the 2-minute compliance quiz to see how your website, social posts and clinic copy score against AHPRA's advertising guidelines.
            </p>
          </div>
          <Button
            asChild
            className="h-11 px-6 text-sm font-semibold bg-primary text-primary-foreground hover:bg-accent-hover rounded-full shrink-0"
          >
            <Link to="/compliance-quiz">Take the compliance quiz →</Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default PractitionerProblem;
